/**
 * The player's server answers 503 when a store cannot say (AP-10): the row and the scheme are retried
 * up to 3 times, waiting what `Retry-After` says (1 s when unusable, at most 8 s). A short storage fault
 * is then a short wait for the learner, not a lesson that never opens.
 */
const TRIES = 3;
const DEFAULT_WAIT_MS = 1_000;
const MAX_WAIT_MS = 8_000;

const waitFor = (retryAfter: string | null): number => {
  const seconds = retryAfter === null ? Number.NaN : Number(retryAfter);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.min(seconds * 1_000, MAX_WAIT_MS) : DEFAULT_WAIT_MS;
};

export async function fetchRetrying503(
  url: string,
  deps: { readonly fetch?: (url: string) => Promise<Response>; readonly wait?: (ms: number) => Promise<void> } = {},
): Promise<Response> {
  const get = deps.fetch ?? ((target: string) => fetch(target));
  const wait = deps.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  let response = await get(url);
  for (let attempt = 0; attempt < TRIES && response.status === 503; attempt += 1) {
    await wait(waitFor(response.headers.get('retry-after')));
    response = await get(url);
  }
  return response;
}
